import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { FileText, ArrowRight, Sparkles, Loader2, Lock } from "lucide-react";

const VARIANTS = {
  business: {
    steps: ["Get my report", "Tell us about your business", "Upload your documents", "We score your business", "Your Score & Report"],
    report: "Capital Readiness Report",
    noun: "business",
    sample: "sample-report.pdf",
    missingPrice: null,
  },
  project: {
    steps: ["Get my report", "Tell us about your project", "Upload your documents", "We score your project", "Your Score & Report"],
    report: "Project Readiness Report",
    noun: "project",
    sample: "sample-prr.pdf",
    missingPrice: "Tell us how much you are raising to see your report price.",
  },
};

// Step 1 of onboarding / intake — why the report matters, sample report, dynamic
// price and (when `onUnlock` is given) the paywall button that starts checkout.
export const ReportValueProp = ({ variant = "business", onUnlock, onTier, unlockBusy }) => {
  const v = VARIANTS[variant] || VARIANTS.business;
  const [tier, setTier] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/founder/credits/balance")
      .then((r) => { setTier(r.data?.tier || null); onTier?.(r.data?.tier || null); })
      .catch(() => {})
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const price = tier?.price_usd ? `KES ${Number(tier.price_usd).toLocaleString()}` : null;
  const sampleUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/${v.sample}`;

  return (
    <section className="mt-6 rounded-2xl border border-[#0A8B85]/20 bg-white p-6 shadow-sm sm:p-8" data-testid="report-value-prop">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Why this matters</p>
      <h2 className="mt-1 text-base font-extrabold text-neutral-900 md:text-lg">Get your {v.report} in 5 easy steps</h2>
      <ol className="mt-3 flex flex-wrap gap-2">
        {v.steps.map((s, i) => (
          <li key={s} className="inline-flex items-center gap-1.5 rounded-full bg-[#0A8B85]/8 px-3 py-1 text-xs font-semibold text-[#0A8B85]">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#0A8B85] text-[10px] font-bold text-white">{i + 1}</span>{s}
          </li>
        ))}
      </ol>
      <p className="mt-4 text-sm leading-relaxed text-neutral-700 md:text-[15px]">
        Your {v.report} is the most honest picture of where your {v.noun} stands with funders — before you
        walk into a single meeting. It tells you your exact score, which funders in our network are the right fit for your
        {" "}{v.noun}, how much capital you could realistically raise, and the likelihood of each funder saying yes. If your score
        is strong, Clinq will connect you directly to funders within our network. If you are not quite there yet, your report
        will tell you exactly what to do to increase your chances of getting funded. No guesswork. No wasted meetings. Just a
        clear path forward.
      </p>
      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <a href={sampleUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-semibold text-neutral-800 hover:border-[#0A8B85] hover:text-[#0A8B85]"
          data-testid="see-sample-report-link">
          <FileText className="h-4 w-4" /> See a sample {v.report} <ArrowRight className="h-3.5 w-3.5" />
        </a>
        {price ? (
          onUnlock ? (
            <div className="flex flex-col items-start gap-1 sm:items-end">
              <button type="button" onClick={onUnlock} disabled={unlockBusy} data-testid="report-unlock-button"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0A8B85] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#0A8B85]/20 transition-colors hover:bg-[#066667] disabled:opacity-60">
                {unlockBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Get my {v.report} for <span className="text-lg font-extrabold">{price}</span>
                <ArrowRight className="h-4 w-4" />
              </button>
              <span className="text-xs text-neutral-500" data-testid="report-unlock-price">
                One-time payment · includes 3 re-run credits · secure Paystack checkout
              </span>
            </div>
          ) : (
            <p className="inline-flex flex-wrap items-baseline gap-x-2 rounded-xl bg-[#0A8B85]/8 px-4 py-2.5 text-sm font-semibold text-neutral-800" data-testid="report-unlock-price">
              <Sparkles className="h-4 w-4 self-center text-[#0A8B85]" /><span className="whitespace-nowrap">Get my {v.report} for</span> <span className="whitespace-nowrap text-xl font-extrabold text-[#0A8B85]">{price}</span>
              <span className="w-full text-xs font-normal text-neutral-500 sm:w-auto">· includes 3 re-run credits</span>
            </p>
          )
        ) : loaded && v.missingPrice ? (
          <p className="rounded-xl bg-amber-50 px-4 py-2.5 text-xs font-semibold text-amber-800" data-testid="report-price-missing">
            {v.missingPrice}
          </p>
        ) : null}
      </div>
      {onUnlock && (
        <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-neutral-500" data-testid="report-paywall-note">
          <Lock className="h-3.5 w-3.5" /> Steps 2–5 open the moment your payment is confirmed.
        </p>
      )}
    </section>
  );
};
